import React from "react";

/** Screen-pixel stroke — stays thin when the SVG is scaled in the toolbar. */
const LINE = {
  stroke: "currentColor",
  strokeWidth: 1.5,
  vectorEffect: "non-scaling-stroke",
  strokeLinecap: "round",
  strokeLinejoin: "round",
  fill: "none",
};

const svgProps = (props) => ({
  viewBox: "0 0 24 24",
  fill: "none",
  "aria-hidden": props["aria-hidden"] ?? true,
  ...props,
});

/**
 * Sign PDF toolbar icons — thin linear line-art (reference geometry).
 */

export function SignPdfDeleteIcon({
  className = "sp-sig__icon",
  ...props
}) {
  return (
    <svg className={className} {...svgProps(props)}>
      {/* handle — small inverted U centred on lid */}
      <path
        d="M 10 5.35 V 4.45 A 1 1 0 0 1 11 3.45 H 13 A 1 1 0 0 1 14 4.45 V 5.35"
        {...LINE}
      />
      {/* lid — wide bar, rounded caps via strokeLinecap */}
      <path d="M 5.5 6.85 H 18.5" {...LINE} />
      {/* body — tapered bucket with rounded bottom corners */}
      <path
        d="M 7.35 7.85 L 7.95 19.15 A 2.1 2.1 0 0 0 10.05 20.65 H 13.95 A 2.1 2.1 0 0 0 16.05 19.15 L 16.65 7.85"
        {...LINE}
      />
      {/* slats — centred, rounded ends, slight taper on outers */}
      <path
        d="M 10.1 10.75 L 9.9 17.25 M 12 10.75 V 17.25 M 13.9 10.75 L 14.1 17.25"
        {...LINE}
      />
    </svg>
  );
}

export function SignPdfDuplicateIcon({
  className = "sp-sig__icon",
  ...props
}) {
  // Lucide-style copy geometry tuned to reference portrait sheets.
  // Back L-path (top-left) drawn first; front sheet (bottom-right) on top.
  const rx = 2.75;

  return (
    <svg className={className} {...svgProps(props)}>
      <path
        d={`M 5.25 17.25
           H 4.25
           A ${rx} ${rx} 0 0 1 2.5 14.5
           V 5.25
           A ${rx} ${rx} 0 0 1 5.25 2.5
           H 8.75`}
        {...LINE}
      />
      <rect x="8.75" y="6.5" width="11.5" height="14" rx={rx} {...LINE} />
    </svg>
  );
}

export function SignPdfDatePickerIcon({
  className = "sp-sig__icon",
  ...props
}) {
  // Compact calendar — same LINE stroke as delete/duplicate, 3×3 day grid.
  const cell = (x, y) => (
    <rect
      key={`${x}-${y}`}
      x={x}
      y={y}
      width="2.7"
      height="2.7"
      rx="0.65"
      {...LINE}
    />
  );

  return (
    <svg className={className} {...svgProps(props)}>
      {/* binder rings */}
      <path
        d="M 8.75 6.25 V 4.85 A 0.85 0.85 0 0 1 9.6 4 H 10.35 A 0.85 0.85 0 0 1 11.2 4.85 V 6.25
           M 12.8 6.25 V 4.85 A 0.85 0.85 0 0 1 13.65 4 H 14.4 A 0.85 0.85 0 0 1 15.25 4.85 V 6.25"
        {...LINE}
      />
      {/* outer frame — inset for a slightly smaller silhouette */}
      <rect x="5.25" y="6.25" width="13.5" height="13.25" rx="2.6" {...LINE} />
      {/* header divider */}
      <path d="M 5.25 10 H 18.75" {...LINE} />
      {/* day grid — 3 + 3 + 3 */}
      {cell(7.15, 11.35)}
      {cell(10.65, 11.35)}
      {cell(14.15, 11.35)}
      {cell(7.15, 14.2)}
      {cell(10.65, 14.2)}
      {cell(14.15, 14.2)}
      {cell(7.15, 17.05)}
      {cell(10.65, 17.05)}
      {cell(14.15, 17.05)}
    </svg>
  );
}

/** Empty checkbox (box only). */
export function SignPdfCheckboxEmptyIcon({
  className = "sp-sig__icon",
  ...props
}) {
  return (
    <svg className={className} {...svgProps(props)}>
      <rect x="5.5" y="5.5" width="13" height="13" rx="2.25" {...LINE} />
    </svg>
  );
}

/** Checked checkbox — box + tick. */
export function SignPdfCheckboxBoxedIcon({
  className = "sp-sig__icon",
  ...props
}) {
  return (
    <svg className={className} {...svgProps(props)}>
      <rect x="5.5" y="5.5" width="13" height="13" rx="2.25" {...LINE} />
      <path d="M8.2 12.1l2.2 2.2 5-5.2" {...LINE} />
    </svg>
  );
}

/** Just tickmark — no box (reference style). */
export function SignPdfCheckboxCheckIcon({
  className = "sp-sig__icon",
  ...props
}) {
  return (
    <svg className={className} {...svgProps(props)}>
      <path d="M6.5 12.25l3.4 3.4 7.6-8.1" {...LINE} />
    </svg>
  );
}

/** Cross mark — no box (reference style). */
export function SignPdfCheckboxWrongIcon({
  className = "sp-sig__icon",
  ...props
}) {
  return (
    <svg className={className} {...svgProps(props)}>
      <path d="M7.25 7.25l9.5 9.5M16.75 7.25l-9.5 9.5" {...LINE} />
    </svg>
  );
}

/** Ungroup a signing block into independent fields. */
export function SignPdfUngroupIcon({
  className = "sp-sig__icon",
  ...props
}) {
  return (
    <svg className={className} {...svgProps(props)}>
      <path d="M8 8l8 8M16 8l-8 8" {...LINE} />
      <rect x="3.5" y="3.5" width="6" height="6" rx="1.2" {...LINE} />
      <rect x="14.5" y="14.5" width="6" height="6" rx="1.2" {...LINE} />
    </svg>
  );
}
